// Paso 4 — ANTES Y DESPUÉS
//
// Gráfica TR60 (Chart.js como módulo). Línea horizontal en 0,79 s.
// Bloque auralización: ANTES / DESPUÉS, voz/música, play/pause grande.

import { state, update } from '/src/shared/state.js';
import { ctaButton } from '/src/ui/components/cta-button.js';
import { flattenResults, bandsForAuralization } from '/src/shared/results-adapter.js';

const TR60_TARGET = 0.79;

async function loadChart() {
  try {
    const mod = await import('chart.js/auto');
    return mod.default || mod.Chart || mod;
  } catch (_err) {
    return null;
  }
}

async function loadAcoustics() {
  try {
    return await import('/src/core/acoustics.js');
  } catch (_err) {
    return null;
  }
}

async function loadAuralization() {
  try {
    return await import('/src/audio/auralization.js');
  } catch (_err) {
    return null;
  }
}

const FALLBACK_FREQUENCIES = [125, 250, 500, 1000, 2000, 4000];

function fallbackTR60(room) {
  if (!room?.largo) return FALLBACK_FREQUENCIES.map(() => 1.5);
  const v = room.largo * room.ancho * room.altura;
  // Curva placeholder visualmente coherente.
  return FALLBACK_FREQUENCIES.map((f) => 0.04 * v * (f < 500 ? 1.4 : 1));
}
function fallbackTR60After(beforeArr) {
  return beforeArr.map((v) => Math.max(0.4, v * 0.45));
}

export function renderStep4(host, { next, back }) {
  host.innerHTML = `
    <header class="step__header">
      <h1 class="step__title" id="step-4-title">Antes y después</h1>
      <p class="step__subtitle">Así cambia el sonido en tu sala con los paneles ISINAC.</p>
    </header>

    <div id="kpi-tr"></div>

    <div class="kpi-row" style="padding:0;">
      <div style="flex:1; padding: 1rem; min-height: 240px;">
        <canvas id="chart-tr60" aria-label="Gráfica TR60 antes y después"></canvas>
      </div>
    </div>

    <section class="aural" aria-labelledby="aural-title">
      <h2 id="aural-title" class="step__title" style="font-size:1.1rem;">Escucha la diferencia</h2>

      <div class="aural__abtoggle" role="group" aria-label="Antes o después">
        <button type="button" class="btn btn--secondary" id="ab-before" aria-pressed="true">ANTES</button>
        <button type="button" class="btn btn--secondary" id="ab-after">DESPUÉS</button>
      </div>

      <div class="aural__source" role="group" aria-label="Tipo de fuente">
        <button type="button" class="btn btn--ghost" id="src-voice" aria-pressed="true">Voz</button>
        <button type="button" class="btn btn--ghost" id="src-music">Música</button>
      </div>

      <button type="button" class="aural__play" id="play-btn" aria-label="Reproducir">▶</button>
    </section>

    <div class="sticky-cta" id="sticky-cta"></div>
  `;

  const kpiHost = host.querySelector('#kpi-tr');
  const canvas = host.querySelector('#chart-tr60');
  const abBefore = host.querySelector('#ab-before');
  const abAfter = host.querySelector('#ab-after');
  const srcVoice = host.querySelector('#src-voice');
  const srcMusic = host.querySelector('#src-music');
  const playBtn = host.querySelector('#play-btn');
  const stickyHost = host.querySelector('#sticky-cta');

  let chart = null;
  let aural = null;
  let mode = 'before'; // 'before' | 'after'
  let source = 'voice'; // 'voice' | 'music'
  let playing = false;

  // Cargamos la auralización en paralelo. El bloque de cálculo la espera abajo.
  const auralReady = loadAuralization().then((mod) => {
    if (!mod || !mod.AuralizationEngine) return null;
    try {
      aural = new mod.AuralizationEngine();
      return aural;
    } catch (err) {
      console.warn('[step4] AuralizationEngine falló:', err);
      return null;
    }
  });

  // Cálculos
  Promise.all([loadChart(), loadAcoustics()]).then(([Chart, ac]) => {
    let freqs = FALLBACK_FREQUENCIES;
    let before = fallbackTR60(state.room);
    let after = fallbackTR60After(before);
    let auralBands = { before: {}, after: {} };

    if (ac) {
      if (Array.isArray(ac.FREQUENCIES)) freqs = ac.FREQUENCIES;
      if (typeof ac.calculateTR60 === 'function') {
        try {
          // Una sola llamada: el módulo ya devuelve trBefore (sin paneles) y trAfter (con paneles).
          const panelsForCalc = state.product
            ? [{ count: state.panels?.length || 0, product: state.product }]
            : [];
          const raw = ac.calculateTR60({
            room: state.room,
            surfaces: state.surfaces,
            panels: panelsForCalc,
            product: state.product,
          });
          const flat = flattenResults(raw);
          if (flat && flat.bands.length) {
            freqs = flat.bands.map((b) => b.freq);
            before = flat.bands.map((b) => b.trBefore);
            after = flat.bands.map((b) => b.trAfter);
            auralBands = bandsForAuralization(raw);
            update({ results: flat });
          }
        } catch (err) {
          console.warn('[step4] calculateTR60 falló:', err);
        }
      }
    }

    renderKPI(before, after);
    if (Chart) renderChart(Chart, freqs, before, after);

    // Configurar auralización con los TR60 reales una vez que el motor cargue.
    auralReady.then((engine) => {
      if (engine?.setTR60) engine.setTR60(auralBands.before, auralBands.after);
    });
  });

  function renderKPI(before, after) {
    const idx500 = 2; // 500 Hz por convención
    const b = before[idx500] ?? before[0];
    const a = after[idx500] ?? after[0];
    const improvement = b > 0 ? Math.round(((b - a) / b) * 100) : 0;
    kpiHost.innerHTML = `
      <div class="kpi-row">
        <div class="kpi" style="flex:1.4;">
          <div class="kpi__label">Tu reverberación</div>
          <div class="kpi__value kpi__value--gold">
            ${fmtSec(b)} → ${fmtSec(a)} s
          </div>
          <div class="kpi__label" style="margin-top:0.25rem;">Mejora del ${improvement}%</div>
        </div>
      </div>
    `;
  }

  function renderChart(Chart, freqs, before, after) {
    if (chart) chart.destroy();
    const ctx = canvas.getContext('2d');
    chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: freqs.map((f) => `${f} Hz`),
        datasets: [
          {
            label: 'Antes',
            data: before,
            borderColor: '#848c9c',
            backgroundColor: 'rgba(132,140,156,0.18)',
            tension: 0.3,
            borderWidth: 2,
            pointRadius: 3,
          },
          {
            label: 'Después',
            data: after,
            borderColor: '#b09845',
            backgroundColor: 'rgba(176,152,69,0.18)',
            tension: 0.3,
            borderWidth: 2.5,
            pointRadius: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { font: { family: 'Poppins' } } },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'TR60 (s)' },
            grid: { color: 'rgba(132,140,156,0.18)' },
          },
          x: { grid: { display: false } },
        },
      },
      plugins: [targetLinePlugin],
    });
  }

  // (auralReady ya se declaró arriba para que el bloque de cálculo pueda usarlo)

  // A/B
  abBefore.addEventListener('click', () => setMode('before'));
  abAfter.addEventListener('click', () => setMode('after'));
  srcVoice.addEventListener('click', () => setSource('voice'));
  srcMusic.addEventListener('click', () => setSource('music'));

  function setMode(m) {
    mode = m;
    abBefore.setAttribute('aria-pressed', String(m === 'before'));
    abAfter.setAttribute('aria-pressed', String(m === 'after'));
    if (aural?.setMode) aural.setMode(m);
  }
  function setSource(s) {
    source = s;
    srcVoice.setAttribute('aria-pressed', String(s === 'voice'));
    srcMusic.setAttribute('aria-pressed', String(s === 'music'));
    if (aural?.setSource) aural.setSource(s);
  }

  playBtn.addEventListener('click', async () => {
    if (!aural) {
      console.info('[step4] auralización no disponible aún');
      return;
    }
    try {
      if (!playing) {
        await aural.play?.({ mode, source });
        playing = true;
        playBtn.textContent = '⏸';
        playBtn.setAttribute('aria-label', 'Pausar');
      } else {
        await aural.pause?.();
        playing = false;
        playBtn.textContent = '▶';
        playBtn.setAttribute('aria-label', 'Reproducir');
      }
    } catch (err) {
      console.warn('[step4] play/pause falló:', err);
    }
  });

  // CTAs
  const backBtn = ctaButton({ label: 'Atrás', variant: 'secondary', onClick: back });
  const nextBtn = ctaButton({ label: 'Pedir presupuesto', variant: 'primary', onClick: next });
  stickyHost.appendChild(backBtn);
  stickyHost.appendChild(nextBtn);

  return () => {
    if (chart) chart.destroy();
    if (aural?.dispose) {
      try {
        aural.dispose();
      } catch (_err) {
        /* ignore */
      }
    }
  };
}

const targetLinePlugin = {
  id: 'tr60Target',
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!scales?.y) return;
    const y = scales.y.getPixelForValue(TR60_TARGET);
    if (y < chartArea.top || y > chartArea.bottom) return;
    ctx.save();
    ctx.strokeStyle = '#b09845';
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.font = '11px Poppins, sans-serif';
    ctx.fillStyle = '#b09845';
    ctx.fillText('Objetivo 0,79 s', chartArea.left + 6, y - 4);
    ctx.restore();
  },
};

function fmtSec(n) {
  return Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
